import React from 'react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { ArrowDropDown } from '@material-ui/icons';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { useSendoutAccountability } from 'features/sendouts/hooks';
import { useFetchWithStatusV2 } from 'hooks/fetchWithStatus';
import strings from 'strings';
import { FPIconButton, When } from 'UI/components/atoms';
import FPChip from 'UI/components/atoms/FPChip';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import EmptyPlaceholder from 'UI/components/templates/EmptyPlaceholder';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { Endpoints } from 'UI/constants/endpoints';
import { UIStatus } from 'UI/constants/status';

import { buildStatusReasonItems, DetailsSummary, StatusReasonsSummary } from '../../shared';
import { buildRelatedEntities } from '../../shared/DetailsSummary/DetailsSummary.utils';

import { REASONS_TITLE_BY_STATUS } from './SendoverDetailsTabs/Reasons/Reasons.constants';
import { SwitchSendoverOptions } from './SendoverDetailsDrawer.constants';
import {
  Header,
  HeaderTitle,
  HeaderTitleContainer,
  LoaderContainer,
  useStyles
} from './SendoverDetailsDrawer.styled';
import { SendoverDetailsTabs } from './SendoverDetailsTabs';

const SENDOVER_DETAILS_COPIES = strings.sendoutsV2.drawers.sendoverDetails;

export const SendoverDetailsDrawer = ({
  sendoverId,
  onClose = () => {},
  onConvertToSendout = () => {},
  onEditDetails = () => {}
}) => {
  const classes = useStyles();

  const { state, refreshData } = useFetchWithStatusV2({
    endpoint: sendoverId ? `${Endpoints.Sendouts}/${sendoverId}?` : null,
    apiVersion: 1
  });

  const { canEditSendout: canManageSendover } = useSendoutAccountability(state.results);

  const isLoading = state.status === UIStatus.Loading;
  const sendoverStatus = state.results?.status || {};
  const declinationReasons = buildStatusReasonItems(state.results?.declination_details);

  const handleConvertToSendout = (event, switchType) => {
    event.preventDefault();
    onConvertToSendout({
      switchType,
      sendover: state.results
    });
  };

  const moreActionsDefinition = [
    {
      title: SENDOVER_DETAILS_COPIES.actions.editDetails,
      visible: canManageSendover,
      action: () => onEditDetails(refreshData)
    }
  ];

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      },
      GridItemProps: { sm: 2 }
    },
    {
      props: {
        text: strings.sendouts.form.buttons.switch,
        disabled: isLoading || !!state?.error,
        icon: () => <ArrowDropDown />,
        iconPosition: 'right',
        menuItems: SwitchSendoverOptions.map(item => ({
          ...item,
          action: event => handleConvertToSendout(event, item.value)
        }))
      },
      mode: 'menu',
      GridItemProps: { sm: 3 },
      visible: canManageSendover
    }
  ];

  const relatedEntities = buildRelatedEntities(state.results);

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, classes: { paper: classes.drawerLarge } }}
      onClose={onClose}
      contentContainerClassName={classes.drawerContentContainer}
      header={
        <Header>
          <HeaderTitleContainer>
            <HeaderTitle variant="h5">{SENDOVER_DETAILS_COPIES.title}</HeaderTitle>
            <FPChip
              size="small"
              aria-label="sendover status chip"
              label={sendoverStatus.title}
              variant="outlined"
              color={sendoverStatus.color}
            />
          </HeaderTitleContainer>
          <FPIconButton
            data-testid={strings.shared.ui.close}
            icon={HighlightOffIcon}
            onClick={onClose}
            tooltipProps={{ title: strings.shared.ui.close }}
          />
        </Header>
      }
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 1 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <When condition={state.status === UIStatus.Error}>
        <EmptyPlaceholder
          subtitle={strings.sendoutsV2.drawers.sendover.details.error}
          height="100%"
        />
      </When>

      <When condition={isLoading}>
        <LoaderContainer>
          <CircularProgress />
        </LoaderContainer>
      </When>
      <When condition={state.status === UIStatus.Success}>
        <div className={classes.drawerSection}>
          <DetailsSummary
            title={state.results?.joborder?.title}
            moreActionsDefinition={moreActionsDefinition}
            relatedEntities={relatedEntities}
          />
          <StatusReasonsSummary
            title={REASONS_TITLE_BY_STATUS[sendoverStatus.id]}
            items={declinationReasons}
          />
        </div>
        <SendoverDetailsTabs sendoverId={sendoverId} />
      </When>
    </FPDrawer>
  );
};
